/* BattleOutcome — Victory / Defeat / Report / Share screens shown at
 * the end of a PvP battle. Everything on screen is derived from
 * SERVER data: `battle.rounds`, `battle.winner`, `battle.round_wins`,
 * `battle.challenger_snapshot`, `battle.opponent_snapshot`. The client
 * never invents damage, crits, or rewards — those are all pulled from
 * `hits[]`, `hp_after` and the `rewards` block if present.
 */
import { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Sword, Shield, Sparkles, Share2, RotateCw, Swords, Home, FileText, ChevronLeft, Zap, Flame, Compass } from 'lucide-react';
import { toast } from 'sonner';

// ---------------------------------------------------------------------
// Helpers — derive summary stats from server rounds[].
// ---------------------------------------------------------------------
export function summarizeBattle(battle, mySide) {
  const rounds = battle?.rounds || [];
  let dealt = 0, taken = 0, crits = 0, misses = 0, abilities = 0;
  let strongest = { value: 0, kind: null };
  let closest = { delta: Infinity, round: null };
  rounds.forEach((r) => {
    (r.hits || []).forEach((h) => {
      if (h.kind === 'damage') {
        if (h.target_side !== mySide) {
          dealt += Math.abs(h.value || 0);
          if ((h.value || 0) > strongest.value) strongest = { value: h.value, kind: h.crit ? 'crit' : 'attack' };
        } else {
          taken += Math.abs(h.value || 0);
        }
        if (h.crit && h.target_side !== mySide) crits += 1;
      }
      if (h.kind === 'miss' && h.target_side !== mySide) misses += 1;
    });
    (r.log || []).forEach((line) => { if (/unleashes/i.test(line)) abilities += 1; });
    if (r.hp_after) {
      const delta = Math.abs((r.hp_after.challenger || 0) - (r.hp_after.opponent || 0));
      if (delta < closest.delta) closest = { delta, round: r.round };
    }
  });
  return { dealt, taken, crits, misses, abilities, strongest, closest };
}

// ---------------------------------------------------------------------
// VICTORY / DEFEAT — very similar layout, different copy + colour.
// ---------------------------------------------------------------------
export default function BattleOutcome({ battle, mySide, onRematch, onExit, onOpenReport, onOpenShare, iWon }) {
  const summary = useMemo(() => summarizeBattle(battle, mySide), [battle, mySide]);
  const oppSide = mySide === 'challenger' ? 'opponent' : 'challenger';
  const mine = mySide === 'challenger' ? battle.challenger_snapshot : battle.opponent_snapshot;
  const opp = oppSide === 'challenger' ? battle.challenger_snapshot : battle.opponent_snapshot;
  const wins = battle.round_wins || {};
  const rewards = battle.rewards || null; // server-provided (may be null)

  const title = iWon ? 'VICTORY' : 'DEFEAT';
  const subtitle = iWon ? 'The wildlife bows to yours.' : 'That was close.';
  const accent = iWon ? '#e9c46a' : '#94a3b8';
  const gradient = iWon
    ? 'from-amber-300 via-yellow-200 to-amber-400'
    : 'from-slate-300 via-slate-200 to-slate-400';

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="absolute inset-0 z-50 bg-[#040A08]/95 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto"
      data-testid={iWon ? 'victory-screen' : 'defeat-screen'}
    >
      {iWon && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          {Array.from({ length: 28 }).map((_, i) => (
            <motion.span
              key={i}
              initial={{ y: -20, opacity: 0, scale: 0.6, x: `${Math.random() * 100}%` }}
              animate={{ y: '110vh', opacity: [0, 1, 1, 0], scale: [0.6, 1, 0.8], rotate: 360 }}
              transition={{ duration: 3.5 + Math.random() * 2, delay: Math.random() * 1.2, repeat: Infinity }}
              className="absolute w-2 h-2 rounded-sm"
              style={{
                left: `${Math.random() * 100}%`,
                background: ['#e9c46a', '#f59e0b', '#facc15', '#84cc16'][i % 4],
              }}
            />
          ))}
        </div>
      )}

      <div className="w-full max-w-md space-y-4 relative">
        <motion.div
          initial={{ scale: 0.6, opacity: 0, y: -20 }}
          animate={{ scale: [0.6, 1.15, 1], opacity: 1, y: 0 }}
          transition={{ duration: 0.6, times: [0, 0.5, 1] }}
          className="text-center space-y-1"
        >
          <p className="text-[10px] uppercase tracking-[0.4em]" style={{ color: accent }}>Rival Battle</p>
          <h1 className={`font-serif text-6xl leading-none bg-gradient-to-b ${gradient} bg-clip-text text-transparent drop-shadow-[0_0_18px_rgba(233,196,106,0.5)]`}>
            {title}
          </h1>
          <p className="text-xs text-white/60 mt-1">{subtitle}</p>
        </motion.div>

        {/* Score line */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
          className="flex items-center justify-center gap-4 py-3"
        >
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-widest text-white/50">{iWon ? 'You' : mySide}</p>
            <p className="font-serif text-lg truncate max-w-[8rem]">{mine?.name}</p>
          </div>
          <div className="flex items-center gap-2">
            <p className="font-serif text-5xl tabular-nums" style={{ color: iWon ? '#4ade80' : '#f87171' }}>{wins[mySide] || 0}</p>
            <span className="text-white/40 text-xs">–</span>
            <p className="font-serif text-5xl tabular-nums text-white/70">{wins[oppSide] || 0}</p>
          </div>
          <div className="text-left">
            <p className="text-[10px] uppercase tracking-widest text-white/50">Opponent</p>
            <p className="font-serif text-lg truncate max-w-[8rem]">{opp?.name}</p>
          </div>
        </motion.div>

        {/* Stat pills */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}
          className="grid grid-cols-2 gap-2"
        >
          <Pill icon={<Sword className="w-3 h-3" />} label="Damage dealt" value={summary.dealt} />
          <Pill icon={<Shield className="w-3 h-3" />} label="Damage taken" value={summary.taken} />
          <Pill icon={<Flame className="w-3 h-3" />} label="Critical hits" value={summary.crits} />
          <Pill icon={<Zap className="w-3 h-3" />} label="Abilities" value={summary.abilities} />
          {summary.strongest.value > 0 && (
            <Pill icon={<Sparkles className="w-3 h-3" />} label="Strongest hit" value={summary.strongest.value} col2 />
          )}
        </motion.div>

        {/* Rewards (server-provided). */}
        {rewards && (
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
            className="rounded-2xl border border-amber-400/30 bg-amber-400/5 p-3"
          >
            <p className="text-[10px] uppercase tracking-[0.25em] text-amber-300 mb-2">Rewards</p>
            <div className="flex flex-wrap gap-2 text-xs">
              {rewards.xp > 0 && <span className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10">+{rewards.xp} XP</span>}
              {rewards.wildcoins > 0 && <span className="px-2 py-0.5 rounded-full bg-amber-400/10 border border-amber-400/30 text-amber-300">+{rewards.wildcoins} WildCoins</span>}
              {rewards.rank_change != null && <span className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10">Rank {rewards.rank_change >= 0 ? '+' : ''}{rewards.rank_change}</span>}
            </div>
          </motion.div>
        )}

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85 }}
          className="grid grid-cols-2 gap-2 pt-1"
        >
          <button
            type="button" onClick={onRematch} data-testid="outcome-rematch"
            className="col-span-2 h-11 rounded-full bg-gradient-to-r from-rose-500 to-orange-500 text-black font-bold text-sm uppercase tracking-widest flex items-center justify-center gap-2"
          >
            <RotateCw className="w-4 h-4" /> Rematch
          </button>
          <button type="button" onClick={onOpenReport} data-testid="outcome-report"
            className="h-10 rounded-full bg-white/5 border border-white/10 text-[10px] uppercase tracking-widest flex items-center justify-center gap-1.5">
            <FileText className="w-3 h-3" /> Battle Report
          </button>
          <button type="button" onClick={onOpenShare} data-testid="outcome-share"
            className="h-10 rounded-full bg-white/5 border border-white/10 text-[10px] uppercase tracking-widest flex items-center justify-center gap-1.5">
            <Share2 className="w-3 h-3" /> Share
          </button>
          <button type="button" onClick={onExit} data-testid="outcome-exit"
            className="col-span-2 h-10 rounded-full border border-white/10 text-[10px] uppercase tracking-widest flex items-center justify-center gap-1.5 text-muted-foreground hover:text-white">
            <Home className="w-3 h-3" /> Return to Explorer
          </button>
        </motion.div>
      </div>
    </motion.div>
  );
}

function Pill({ icon, label, value, col2 = false }) {
  return (
    <div className={`rounded-xl bg-white/5 border border-white/10 p-2.5 flex items-center gap-2 ${col2 ? 'col-span-2' : ''}`}>
      <span className="text-white/60">{icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-[9px] uppercase tracking-widest text-white/50 truncate">{label}</p>
        <p className="font-mono tabular-nums text-lg">{value}</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// BattleReport — round-by-round timeline. Renders the raw log with
// derived per-round summaries.
// ---------------------------------------------------------------------
export function BattleReport({ battle, mySide, onClose }) {
  const oppSide = mySide === 'challenger' ? 'opponent' : 'challenger';
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="absolute inset-0 z-[60] bg-[#040A08]/97 backdrop-blur-md overflow-y-auto"
      data-testid="battle-report"
    >
      <div className="max-w-md mx-auto p-4 space-y-4 pb-16">
        <div className="flex items-center gap-2">
          <button type="button" onClick={onClose} data-testid="report-close"
            className="p-1.5 rounded-full hover:bg-white/5">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-emerald-300">Battle Report</p>
            <p className="font-serif text-2xl">Round-by-round timeline</p>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/5 p-3 text-xs font-mono tabular-nums grid grid-cols-3 gap-2 text-center">
          <div><p className="text-white/50 text-[9px] uppercase tracking-widest">Winner</p><p className="mt-0.5">{battle.winner_user_id === (battle.challenger_owner_id) ? 'Challenger' : (battle.winner === 'challenger' ? battle.challenger_snapshot?.name : battle.opponent_snapshot?.name)}</p></div>
          <div><p className="text-white/50 text-[9px] uppercase tracking-widest">Arena</p><p className="mt-0.5">{battle.arena || 'Wild'}</p></div>
          <div><p className="text-white/50 text-[9px] uppercase tracking-widest">Weather</p><p className="mt-0.5">{battle.weather || 'Clear'}</p></div>
        </div>

        {(battle.rounds || []).map((r) => {
          const won = r.winner === mySide;
          return (
            <div key={r.round} className={`rounded-2xl border p-3 space-y-2 ${
              won ? 'border-emerald-400/30 bg-emerald-500/5' : 'border-rose-400/25 bg-rose-500/5'
            }`}>
              <div className="flex items-center justify-between">
                <p className="font-serif text-lg">Round {r.round}</p>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
                  won ? 'bg-emerald-500/20 text-emerald-300' : 'bg-rose-500/20 text-rose-300'
                }`}>
                  {won ? 'Round Won' : 'Round Lost'}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-[10px] font-mono tabular-nums text-white/70">
                <div>{battle.challenger_snapshot?.name}: <span className="text-white">{r.hp_after?.challenger ?? '—'}</span> / {r.hp_max?.challenger ?? '—'} HP</div>
                <div className="text-right">{battle.opponent_snapshot?.name}: <span className="text-white">{r.hp_after?.opponent ?? '—'}</span> / {r.hp_max?.opponent ?? '—'} HP</div>
              </div>
              <ol className="space-y-0.5 text-xs text-white/70 leading-relaxed">
                {(r.log || []).map((line, i) => (
                  <li key={i} className={/critical|unleashes|takes the round/i.test(line) ? 'text-white' : ''}>
                    · {line}
                  </li>
                ))}
              </ol>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}

// ---------------------------------------------------------------------
// ShareResultCard — the redesigned share card, matching the brief:
//   WILDQUEST
//   DENIS  3  VS  2  ALEX
//   VICTORY
//   Great Blue Heron  ·  Captured by Denis
//   wildquest.ca
// ---------------------------------------------------------------------
export function ShareResultCard({ battle, mySide, myNickname, oppNickname, onClose }) {
  const [copied, setCopied] = useState(false);
  const oppSide = mySide === 'challenger' ? 'opponent' : 'challenger';
  const mine = mySide === 'challenger' ? battle.challenger_snapshot : battle.opponent_snapshot;
  const opp = oppSide === 'challenger' ? battle.challenger_snapshot : battle.opponent_snapshot;
  const wins = battle.round_wins || {};
  const iWon = battle.winner === mySide;

  const shareText = `${myNickname || 'Explorer'} ${wins[mySide] || 0}–${wins[oppSide] || 0} ${oppNickname || 'Rival'}. My ${mine?.name} ${iWon ? 'defeated' : 'battled'} their ${opp?.name} on WildQuest. https://wildquest.ca`;

  const share = async () => {
    const shareUrl = `${window.location.origin}/battle/live/${battle.battle_id}`;
    if (navigator.share) {
      try { await navigator.share({ title: 'WildQuest Rival Battle', text: shareText, url: shareUrl }); }
      catch { /* user cancelled */ }
      return;
    }
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      setCopied(true); toast.success('Copied — paste anywhere.');
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Copy failed'); }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="absolute inset-0 z-[70] bg-black/85 backdrop-blur-md flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
      data-testid="share-modal"
    >
      <motion.div
        onClick={(e) => e.stopPropagation()}
        initial={{ y: 40, opacity: 0, scale: 0.94 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        className="w-full max-w-sm space-y-3"
      >
        {/* The share card */}
        <div
          className="relative aspect-[9/16] rounded-3xl overflow-hidden border-2"
          style={{
            borderColor: iWon ? '#e9c46a' : '#94a3b8',
            background: 'linear-gradient(180deg, #0a1a14 0%, #04100a 100%)',
          }}
          data-testid="share-card"
        >
          {/* Hero image — challenger wildlife photo */}
          <div className="absolute inset-x-0 top-0 h-1/2 overflow-hidden">
            {mine?.picture && (
              <img src={mine.picture} alt="" className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.opacity = '0.3'; }} />
            )}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#04100a]" />
          </div>

          <div className="absolute inset-0 flex flex-col justify-between p-5">
            <div className="text-center">
              <p className="text-[10px] uppercase tracking-[0.5em] text-white/80 font-bold drop-shadow">WILDQUEST</p>
            </div>

            <div className="text-center space-y-4">
              <div className="flex items-center justify-center gap-3">
                <div className="text-right">
                  <p className="font-serif text-xl truncate max-w-[7rem]">{myNickname || 'You'}</p>
                </div>
                <p className="font-serif text-5xl tabular-nums" style={{ color: iWon ? '#e9c46a' : '#f87171' }}>{wins[mySide] || 0}</p>
                <p className="text-white/40 text-sm uppercase tracking-widest">VS</p>
                <p className="font-serif text-5xl tabular-nums text-white/70">{wins[oppSide] || 0}</p>
                <div className="text-left">
                  <p className="font-serif text-xl truncate max-w-[7rem]">{oppNickname || 'Rival'}</p>
                </div>
              </div>

              <p className={`font-serif text-4xl leading-none ${iWon ? 'text-amber-300' : 'text-slate-300'}`}
                 style={{ textShadow: iWon ? '0 0 24px rgba(233,196,106,0.6)' : 'none' }}>
                {iWon ? 'VICTORY' : 'FOUGHT WELL'}
              </p>
            </div>

            <div className="text-center space-y-1">
              <p className="font-serif text-base">{mine?.name || 'Wildlife'}</p>
              <p className="text-[10px] uppercase tracking-widest text-white/60">
                Captured by {myNickname || 'Explorer'}
              </p>
              <p className="text-[10px] uppercase tracking-[0.3em] text-emerald-300/80 mt-2">wildquest.ca</p>
            </div>
          </div>
        </div>

        <button
          type="button" onClick={share} data-testid="share-send"
          className="w-full h-11 rounded-full bg-gradient-to-r from-emerald-400 to-teal-500 text-black font-bold uppercase tracking-widest text-sm flex items-center justify-center gap-2"
        >
          <Share2 className="w-4 h-4" />
          {copied ? 'Copied!' : 'Share Result'}
        </button>
        <button type="button" onClick={onClose} data-testid="share-close"
          className="w-full h-9 rounded-full border border-white/10 text-[10px] uppercase tracking-widest text-muted-foreground">
          Close
        </button>
      </motion.div>
    </motion.div>
  );
}
